import React, { useEffect, useState } from "react";
import {
  Link,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import { ACTIONITEMVALIDATIONS, CONTACTPAGEVALIDATIONS } from "../../constants/messages";
import ReCAPTCHA from "react-google-recaptcha";
import useApi from "../../hooks/useApi";
import { VerifyCaptcha } from "../../services/userService";
import CloseIcon from "@mui/icons-material/Close";
import ContactPage from "../../pages/contacts/ContactPage";
import { getPortalSecretValue } from "../../services/keyVaultService";

interface EmployeeCheckProps {
  isSignin: boolean;
}

export default function EmployeeCheck({ isSignin }: EmployeeCheckProps) {
  const [displayEmployee, setDisplayEmployee] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [showContactPage, setShowContactPage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [requestFormSnackbarOpen, setrequestFormSnackbarOpen] = useState<boolean>(false);
  const { executeApi } = useApi();
  const [captchaSecretKey, setCaptchaSecretKey] = useState("");

  const handleRequestFormSnackbarClose = () => setrequestFormSnackbarOpen(false);

const handleVerify = (token: string | null) => {
    setCaptchaToken(token);
};

  const handleTroubleSignin = () => {
    setDisplayEmployee(true);
  };

  const handleEmployeePopup = (confirmed: boolean) => {
    setDisplayEmployee(false);
    if (confirmed) {
      setShowCaptcha(true);
    } else {
      setErrorMessage(CONTACTPAGEVALIDATIONS.HR_REACH);
      setrequestFormSnackbarOpen(true);
      setTimeout(() => {
        window.location.href = CONTACTPAGEVALIDATIONS.HAMASPIK_CAREERS;
      }, 2000);
    }
  };

  const handleCaptchaSubmit = async () => {
    const success = await executeApi<string, boolean>(VerifyCaptcha, captchaToken);

    if (success) {
      setShowCaptcha(false);
      setShowContactPage(true);
    }
  };

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setrequestFormSnackbarOpen(false);
        setErrorMessage("");
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    async function initGoogleCAPTCHA() {
      try {
        const secrets = await getPortalSecretValue();
        if (secrets.CaptchaSiteKey) {
          setCaptchaSecretKey(secrets.CaptchaSiteKey);
        } else {
          console.warn("CAPTCHA Site key not found.");
        }
      } catch (error) {
        console.error("Failed to load secrets:", error);
      }
    }
    initGoogleCAPTCHA();
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Link
        id="contactLink"
        variant="body2"
        sx={{ alignSelf: "center", cursor: "pointer" }}
        onClick={handleTroubleSignin}
      >
        {isSignin ? "Having trouble logging in?" : "  Need help signing up?"}
      </Link>

      {/* Confirmation dialog */}
      <ConfirmationDialog
        open={displayEmployee}
        title={ACTIONITEMVALIDATIONS.CONFIRM}
        message={CONTACTPAGEVALIDATIONS.EMPLOYEE_RESOURCE}
        onClose={handleEmployeePopup}
        onDiscard={() => setDisplayEmployee(false)}
      />

      {/* Captcha dialog */}
      <Dialog open={showCaptcha} onClose={() => setShowCaptcha(false)} maxWidth="xs">
        <DialogTitle>Captcha Verification</DialogTitle>
        <DialogContent dividers>
          <div className="captcha-container">
            <ReCAPTCHA sitekey={captchaSecretKey} onChange={handleVerify} className="mt-1" />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowCaptcha(false)}
            color="light"
            variant="contained"
            size="small"
          >
            Cancel
          </Button>
          <Button id="captchaSubmit" variant="contained" onClick={handleCaptchaSubmit} size="small" disabled={!captchaToken}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showContactPage}
        onClose={() => setShowContactPage(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Contact Support{" "}
          <IconButton
            aria-label="close"
            onClick={() => setShowContactPage(false)}
            className="dialog-close-button"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <ContactPage onSubmitSuccess={() => setShowContactPage(false)} />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={requestFormSnackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={handleRequestFormSnackbarClose}
      >
        <Alert onClose={handleRequestFormSnackbarClose} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
